import React, { useState, useEffect } from 'react';
import { Fab, Zoom, useTheme } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Navbar from './components/Navbar';
import AboutUs from './components/AboutUs';
import Hero from './components/Hero';
import Services from './components/Services';
import PatientInfo from './components/PatientInfo';
import ContactUs from './components/ContactUs';
import Testimonials from './components/Testimonials';
import Footer from './components/Footer';
import MedicalGallery from './components/MedicalGallery';
import Orthopedic from './components/Orthopedic';
import EndoscopyColonoscopySection from './components/EndoscopyColonoscopy';
import Seo from '../../components/seo/Seo';
import StructuredData from '../../components/seo/StructuredData';
import BreadcrumbsNav from '../../components/BreadcrumbsNav';
import {
  siteUrl,
  logoUrl,
  phone,
  address,
  openingHoursSpecification,
  sameAs,
} from '../../components/seo/config';

const MedicalPage: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const theme = useTheme();
  const medicalSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalBusiness",
    name: "Drax Hall Medical & Surgical",
    url: `${siteUrl}/medical`,
    logo: logoUrl,
    image: logoUrl,
    telephone: phone,
    address,
    openingHoursSpecification,
    sameAs,
    medicalSpecialty: ["PrimaryCare", "Orthopedic", "Cardiology", "Gastroenterology"],
    areaServed: "JM",
  };

  const medicalFaq = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What are your hours of operation?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "We are open from 8 am to 6 pm.",
        },
      },
      {
        "@type": "Question",
        name: "How do I contact the facility?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Call (876) 972-0514 or WhatsApp (876) 236-5215. Email: draxhallmedical@gmail.com.",
        },
      },
      {
        "@type": "Question",
        name: "What type of insurance do you accept?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "We accept major providers including Sagicor, Guardian Life, and Canopy.",
        },
      },
      {
        "@type": "Question",
        name: "How do I schedule an appointment?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Call (876) 972-0514, WhatsApp (876) 236-5215, or email draxhallmedical@gmail.com.",
        },
      },
      {
        "@type": "Question",
        name: "What is your cancellation policy?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Please call us if you cannot make your appointment.",
        },
      },
      {
        "@type": "Question",
        name: "Can I request a special doctor or specialist?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes, you may request a specific doctor or surgeon when scheduling.",
        },
      },
      {
        "@type": "Question",
        name: "How do I obtain my medical records?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Contact us directly. If a company requests on your behalf, they must provide signed consent.",
        },
      },
      {
        "@type": "Question",
        name: "How do I pay my bill?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "In-office by cash, debit, or credit card, or via direct deposit to NCB (Savings Account 544571575).",
        },
      },
      {
        "@type": "Question",
        name: "What should I bring to my appointment?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Bring your ID, insurance card, prescriptions, medication list, and any reports, CDs, X-rays, or ultrasounds.",
        },
      },
      {
        "@type": "Question",
        name: "How long will the appointment take?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Duration depends on visit type, doctor, and whether procedures are needed.",
        },
      },
      {
        "@type": "Question",
        name: "Can I bring a family member or friend?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes, you may bring someone with you to your appointment.",
        },
      },
    ],
  };

  // Track scrolling to toggle visibility of the scroll to top button
  const handleScroll = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    if (scrollTop > 300) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  };

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      <Seo
        title="Medical & Surgical Services | Drax Hall Health Group"
        description="Comprehensive medical and surgical care in Drax Hall, including orthopedics, cardiology, gastroenterology, endoscopy, imaging, and same-day appointments from trusted specialists."
        canonicalPath="/medical"
        ogImage="/logo.png"
      />
      <StructuredData data={[medicalSchema, medicalFaq]} />
      <Navbar />
      <BreadcrumbsNav
        items={[
          { label: "Home", path: "/" },
          { label: "Medical & Surgical", path: "/medical" },
        ]}
      />
      <Hero />
      <AboutUs />
      <Services />
      <Orthopedic/>
      <EndoscopyColonoscopySection/>
      <MedicalGallery />
      <PatientInfo />
      <ContactUs />
      <Testimonials />
      <Footer />

      {/* Scroll to Top Button */}
      <Zoom in={isVisible}>
        <Fab
          color="secondary"
          size="small"
          onClick={scrollToTop}
          aria-label="scroll back to top"
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 100,
            backgroundColor: theme.palette.accent.main,
            '&:hover': {
              backgroundColor: '#a00d56',
            },
          }}
        >
          <KeyboardArrowUpIcon />
        </Fab>
      </Zoom>
    </>
  );
};

export default MedicalPage;
